import React, { useEffect, useState } from 'react';
import SectionBasePage from 'components/SectionBasePage';
import { useDispatch, useSelector } from 'hooks/store';
import {
  cleanVehiclesAction,
  createVehicleAction,
  vehiclesEntitiesSelector,
  vehiclesIdsSelector,
  getVehicleListAction,
  isLoadingVehiclesSelector,
  remoteTotalSelector,
  updateVehicleAction,
} from 'state/slices/vehicle';
import { usePagination } from 'hooks/pagination';
import { Vehicle, PaginationData } from 'constants/interface';
import { Table } from 'UI';
import { Form } from 'antd';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { CreateVehicleData } from 'api/vehicle';

import { unwrapResult } from '@reduxjs/toolkit';
import EditModal, { VehicleFormData } from './components/EditModal';
import Extra from './components/Extra';

import {
  ActionButton,
} from './style';

const VehiclePage = (): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(remoteTotalSelector);

  const vehicleEntities = useSelector(vehiclesEntitiesSelector);
  const vehicleIds = useSelector(vehiclesIdsSelector);
  const isRemoteLoading = useSelector(isLoadingVehiclesSelector);
  const [isLoading, setLoading] = useState(false);

  const vehicles = vehicleIds.map((vehicleId) => {
    const entity = vehicleEntities[vehicleId];
    if (!entity) return undefined;
    const tableVehicle: Vehicle & {
      key?: string,
    } = {
      ...entity,
      key: vehicleId.toString(),
    };
    return tableVehicle;
  }).filter((tableVehicle) => !!tableVehicle) as (Vehicle & { key: string})[];

  const remoteFetch = (
    paginationData: PaginationData,
  ) => {
    dispatch(getVehicleListAction(paginationData));
  };
  const pagination = usePagination<object>( // eslint-disable-line @typescript-eslint/ban-types
    remoteFetch,
    {
      filter: {
        isActive: true,
      },
    },
  );

  useEffect(() => {
    dispatch(cleanVehiclesAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  const [vehicleForm] = Form.useForm<VehicleFormData>();

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [activeEditVehicle, setActiveEditVehicle] = useState<Vehicle>();

  const openModal = (vehicleId: string) => {
    const vehicleEntity = vehicleEntities[vehicleId];
    setActiveEditVehicle(vehicleEntity);
    setEditModalOpen(true);
  };

  const closeModal = () => {
    setActiveEditVehicle(undefined);
    setEditModalOpen(false);
    vehicleForm.resetFields();
  };

  const submitModal = async () => {
    try {
      await vehicleForm.validateFields();
    } catch (e) {
      const errorAlert = createAlertNotification('Verifique os campos com erro.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    const formData: VehicleFormData = vehicleForm.getFieldsValue();

    const vehicleData: CreateVehicleData = {
      ...formData,
      isActive: true,
    };

    try {
      setLoading(true);
      if (activeEditVehicle) {
        const updateResult = await dispatch(updateVehicleAction({
          id: activeEditVehicle.id,
          data: {
            ...vehicleData,
          },
        }));
        unwrapResult(updateResult);
      } else {
        const createResult = await dispatch(createVehicleAction(vehicleData));
        unwrapResult(createResult);
      }
      const successAlert = createAlertNotification(`
      Veículo ${activeEditVehicle ? 'atualizado' : 'criado'} com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      closeModal();
      pagination.fetch();
    } catch (err) {
      const errorAlert = createAlertNotification('Erro interno ao tentar criar veículo. Tente novamente.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  return (
    <SectionBasePage extra={<Extra onAddClick={() => openModal('')} />}>
      <>
        <Table
          dataSource={vehicles}
          pagination={pagination.getInternal()}
          loading={isRemoteLoading}
        >
          <Table.Column
            title="Nome"
            key="name"
            dataIndex="name"
          />
          <Table.Column
            title="Placa"
            key="plate"
            dataIndex="plate"
          />
          <Table.Column
            title="Capacidade"
            key="capacity"
            dataIndex="capacity"
          />
          <Table.Column
            title=""
            width="150px"
            dataIndex="id"
            render={(vehicleId: string) => (
              <ActionButton onClick={() => openModal(vehicleId)}>
                editar »
              </ActionButton>
            )}
          />
        </Table>
        <EditModal
          formHandler={vehicleForm}
          onSubmit={submitModal}
          onCancel={closeModal}
          curVehicle={activeEditVehicle}
          isLoading={isLoading}
          isVisible={isEditModalOpen}
        />
      </>
    </SectionBasePage>
  );
};

export default VehiclePage;
